import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';

type Claim = { id: number; subject: string; status: string; priority: string; createdAt: string; registration: { serialNumber: string; product: { name: string } | null } | null };

const STATUS_COLORS: Record<string, string> = {
  open:      'bg-blue-100 text-blue-700',
  in_review: 'bg-yellow-100 text-yellow-700',
  approved:  'bg-green-100 text-green-700',
  rejected:  'bg-red-100 text-red-700',
  resolved:  'bg-gray-100 text-gray-500',
};

const PRIORITY_COLORS: Record<string, string> = {
  low:    'bg-gray-100 text-gray-500',
  normal: 'bg-blue-50 text-blue-600',
  high:   'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export default function ClaimsPage() {
  const { t } = useTranslation();
  const [claims, setClaims]   = useState<Claim[]>([]);
  const [filter, setFilter]   = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<Claim[]>('/claims').then(r => { setClaims(r); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const visible = filter === 'all' ? claims : claims.filter(c => c.status === filter);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('claims.title')}</h1>
          <p className="text-gray-500 text-sm mt-1">{claims.length} claim{claims.length !== 1 ? 's' : ''}</p>
        </div>
        <Link to="/claims/new" className="btn-primary">{t('claims.newClaim')}</Link>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {(['all', 'open', 'in_review', 'approved', 'rejected', 'resolved'] as const).map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${filter === s ? 'bg-brand-red text-white border-brand-red' : 'bg-white text-gray-500 border-gray-200 hover:border-brand-line'}`}>
            {s === 'all' ? 'All' : t(`status.${s}`)}
          </button>
        ))}
      </div>

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-gray-400">{t('common.loading')}</p>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-gray-400 text-sm">{t('claims.none')}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="w-full text-sm" style={{ minWidth: 580 }}>
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wide">
                  <th className="px-6 py-3">{t('claims.subject')}</th>
                  <th className="px-6 py-3">Product / Serial</th>
                  <th className="px-6 py-3">{t('claims.priority')}</th>
                  <th className="px-6 py-3">{t('claims.status')}</th>
                  <th className="px-6 py-3">{t('claims.created')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visible.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors cursor-pointer">
                    <td className="px-6 py-3">
                      <Link to={`/claims/${c.id}`} className="font-medium hover:text-brand-red">{c.subject}</Link>
                    </td>
                    <td className="px-6 py-3 text-gray-500">
                      <span>{c.registration?.product?.name ?? '—'}</span>
                      <br/><span className="font-mono text-xs">{c.registration?.serialNumber}</span>
                    </td>
                    <td className="px-6 py-3"><span className={`badge ${PRIORITY_COLORS[c.priority] ?? ''}`}>{c.priority}</span></td>
                    <td className="px-6 py-3"><span className={`badge ${STATUS_COLORS[c.status] ?? ''}`}>{t(`status.${c.status}`)}</span></td>
                    <td className="px-6 py-3 text-gray-400">{c.createdAt.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
